/**
 * @engine/vfx — VFX preset builders (effects group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts to
 * keep that module focused on content-type / importer / serialization.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Black Hole - Multi-system effect with accretion disk, relativistic jets, and Hawking radiation.
 *
 * Systems:
 * 1. Accretion Disk - Particles spiral inward with vortex motion, HDR orange/white colors
 * 2. Jet Up - Relativistic jet shooting upward, purple/blue HDR
 * 3. Jet Down - Relativistic jet shooting downward
 * 4. Event Horizon - Dark core particles, very dim
 * 5. Hawking Radiation - Rare bright escaping particles
 */

export function createExplosion2DVFX(
  name: string = "Explosion 2D",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "explosion_2d",
    name: "Explosion 2D",
    nodes: [
      // ===================== FLASH SYSTEM =====================
      // Burst spawn - all at once
      {
        id: "spawn_flash",
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 3 },
        contextId: "ctx_spawn_flash",
      },
      // Very short lifetime = instant pop
      {
        id: "init_flash_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.08, max: 0.15 },
        contextId: "ctx_init_flash",
      },
      // Small random offset
      {
        id: "init_flash_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 0.3,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_flash",
      },
      // Big initial size
      {
        id: "init_flash_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 3, max: 5 },
        contextId: "ctx_init_flash",
      },
      // Blinding white-yellow HDR
      {
        id: "init_flash_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 4, g: 3.5, b: 2, a: 1 } },
              { t: 1, color: { r: 3, g: 2.5, b: 1, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_flash",
      },
      // Flash rapidly shrinks and fades
      {
        id: "update_flash_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 50 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.3, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_flash",
      },
      {
        id: "update_flash_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.5 },
              { t: 0.15, v: 1.2 },
              { t: 1, v: 0.1 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_flash",
      },
      {
        id: "output_flash",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, facingMode: 0 },
        contextId: "ctx_output_flash",
      },

      // ===================== FIREBALL SYSTEM =====================
      {
        id: "spawn_fireball",
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 80 },
        contextId: "ctx_spawn_fireball",
      },
      {
        id: "init_fireball_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.4, max: 0.9 },
        contextId: "ctx_init_fireball",
      },
      // Particles emanate from center
      {
        id: "init_fireball_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 150 },
        inputValues: { speed: 6, speedVariance: 4 },
        contextId: "ctx_init_fireball",
      },
      {
        id: "init_fireball_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 0.6, max: 1.4 },
        contextId: "ctx_init_fireball",
      },
      // Hot core gradient: bright yellow → orange → deep red
      {
        id: "init_fireball_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 2.5, g: 2, b: 0.5, a: 1 } },
              { t: 0.3, color: { r: 2, g: 1.2, b: 0.2, a: 1 } },
              { t: 0.7, color: { r: 1.5, g: 0.4, b: 0.05, a: 1 } },
              { t: 1, color: { r: 0.8, g: 0.15, b: 0.02, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_fireball",
      },
      {
        id: "init_fireball_angular",
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 450 },
        inputValues: { speed: 90, variance: 180 },
        contextId: "ctx_init_fireball",
      },
      // Drag slows the expansion
      {
        id: "update_fireball_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 50 },
        inputValues: { coefficient: 3 },
        contextId: "ctx_update_fireball",
      },
      {
        id: "update_fireball_rotation",
        type: "vfx.update.rotation",
        position: { x: 50, y: 150 },
        inputValues: {},
        contextId: "ctx_update_fireball",
      },
      // Fireball grows then shrinks
      {
        id: "update_fireball_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.3 },
              { t: 0.15, v: 1.2 },
              { t: 0.5, v: 1 },
              { t: 1, v: 0.4 },
            ],
          },
          scale: 1.5,
        },
        contextId: "ctx_update_fireball",
      },
      // Color darkens over life
      {
        id: "update_fireball_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 2.5, g: 2, b: 0.8, a: 1 } },
              { t: 0.2, color: { r: 2, g: 1, b: 0.2, a: 1 } },
              { t: 0.5, color: { r: 1, g: 0.3, b: 0.05, a: 0.9 } },
              { t: 0.8, color: { r: 0.3, g: 0.08, b: 0.02, a: 0.5 } },
              { t: 1, color: { r: 0.1, g: 0.03, b: 0.01, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_fireball",
      },
      {
        id: "output_fireball",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, facingMode: 0 },
        contextId: "ctx_output_fireball",
      },

      // ===================== SHOCKWAVE RING =====================
      {
        id: "spawn_ring",
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 60 },
        contextId: "ctx_spawn_ring",
      },
      {
        id: "init_ring_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.2, max: 0.4 },
        contextId: "ctx_init_ring",
      },
      // Ring particles start on a circle and fly outward
      {
        id: "init_ring_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 0.5,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_ring",
      },
      {
        id: "init_ring_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 250 },
        inputValues: { speed: 18, speedVariance: 4 },
        contextId: "ctx_init_ring",
      },
      {
        id: "init_ring_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.15, max: 0.35 },
        contextId: "ctx_init_ring",
      },
      // White-hot fading to orange
      {
        id: "init_ring_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 3, g: 2.5, b: 1.5, a: 1 } },
        contextId: "ctx_init_ring",
      },
      {
        id: "update_ring_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 50 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.4, v: 0.7 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_ring",
      },
      {
        id: "update_ring_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.5, v: 0.6 },
              { t: 1, v: 0.1 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_ring",
      },
      {
        id: "output_ring",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, facingMode: 0 },
        contextId: "ctx_output_ring",
      },

      // ===================== SPARKS / DEBRIS =====================
      {
        id: "spawn_sparks",
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 120 },
        contextId: "ctx_spawn_sparks",
      },
      {
        id: "init_sparks_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.5, max: 1.2 },
        contextId: "ctx_init_sparks",
      },
      // High speed outward
      {
        id: "init_sparks_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 150 },
        inputValues: { speed: 16, speedVariance: 10 },
        contextId: "ctx_init_sparks",
      },
      // Small bright particles
      {
        id: "init_sparks_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 0.04, max: 0.12 },
        contextId: "ctx_init_sparks",
      },
      {
        id: "init_sparks_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 3, g: 2.8, b: 1.5, a: 1 } },
              { t: 0.5, color: { r: 2.5, g: 1.5, b: 0.3, a: 1 } },
              { t: 1, color: { r: 1.5, g: 0.5, b: 0.1, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_sparks",
      },
      // Gravity pulls sparks down
      {
        id: "update_sparks_gravity",
        type: "vfx.update.gravity",
        position: { x: 50, y: 50 },
        inputValues: { force: { x: 0, y: -12, z: 0 } },
        contextId: "ctx_update_sparks",
      },
      {
        id: "update_sparks_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 150 },
        inputValues: { coefficient: 1.5 },
        contextId: "ctx_update_sparks",
      },
      {
        id: "update_sparks_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.6, v: 0.9 },
              { t: 0.85, v: 0.4 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_sparks",
      },
      {
        id: "output_sparks",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, facingMode: 0 },
        contextId: "ctx_output_sparks",
      },

      // ===================== SMOKE TRAILS =====================
      {
        id: "spawn_smoke",
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 40 },
        contextId: "ctx_spawn_smoke",
      },
      {
        id: "init_smoke_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 1.0, max: 2.5 },
        contextId: "ctx_init_smoke",
      },
      {
        id: "init_smoke_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 1,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_smoke",
      },
      // Slow upward drift
      {
        id: "init_smoke_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 1, z: 0 }, speed: 3, spread: 45 },
        contextId: "ctx_init_smoke",
      },
      {
        id: "init_smoke_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.8, max: 1.8 },
        contextId: "ctx_init_smoke",
      },
      // Dark smoke starting slightly warm-tinted
      {
        id: "init_smoke_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 0.25, g: 0.2, b: 0.15, a: 0.7 } },
        contextId: "ctx_init_smoke",
      },
      {
        id: "init_smoke_angular",
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 550 },
        inputValues: { speed: 15, variance: 30 },
        contextId: "ctx_init_smoke",
      },
      // Curl noise for organic swirling
      {
        id: "update_smoke_curl",
        type: "vfx.update.curl_noise",
        position: { x: 50, y: 50 },
        inputValues: { intensity: 3, scale: 0.4, speed: 0.3 },
        contextId: "ctx_update_smoke",
      },
      {
        id: "update_smoke_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 150 },
        inputValues: { coefficient: 0.8 },
        contextId: "ctx_update_smoke",
      },
      {
        id: "update_smoke_rotation",
        type: "vfx.update.rotation",
        position: { x: 50, y: 250 },
        inputValues: {},
        contextId: "ctx_update_smoke",
      },
      // Smoke grows over time
      {
        id: "update_smoke_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.4 },
              { t: 0.3, v: 1 },
              { t: 0.7, v: 1.5 },
              { t: 1, v: 2 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_smoke",
      },
      // Fade from warm gray to transparent
      {
        id: "update_smoke_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.3, g: 0.22, b: 0.15, a: 0.6 } },
              { t: 0.3, color: { r: 0.2, g: 0.18, b: 0.15, a: 0.5 } },
              { t: 0.7, color: { r: 0.15, g: 0.13, b: 0.12, a: 0.3 } },
              { t: 1, color: { r: 0.1, g: 0.1, b: 0.1, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_smoke",
      },
      {
        id: "output_smoke",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 0, softParticle: true },
        contextId: "ctx_output_smoke",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      // --- Flash ---
      {
        id: "ctx_spawn_flash",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_flash"],
        collapsed: false,
      },
      {
        id: "ctx_init_flash",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "init_flash_lifetime",
          "init_flash_position",
          "init_flash_size",
          "init_flash_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_flash",
        type: "vfx.update",
        position: { x: 50, y: 650 },
        size: { width: 280, height: 200 },
        nodeIds: ["update_flash_alpha", "update_flash_size"],
        collapsed: false,
      },
      {
        id: "ctx_output_flash",
        type: "vfx.output",
        position: { x: 50, y: 900 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_flash"],
        collapsed: false,
      },
      // --- Fireball ---
      {
        id: "ctx_spawn_fireball",
        type: "vfx.spawn",
        position: { x: 400, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_fireball"],
        collapsed: false,
      },
      {
        id: "ctx_init_fireball",
        type: "vfx.initialize",
        position: { x: 400, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_fireball_lifetime",
          "init_fireball_velocity",
          "init_fireball_size",
          "init_fireball_color",
          "init_fireball_angular",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_fireball",
        type: "vfx.update",
        position: { x: 400, y: 750 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "update_fireball_drag",
          "update_fireball_rotation",
          "update_fireball_size",
          "update_fireball_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_fireball",
        type: "vfx.output",
        position: { x: 400, y: 1200 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_fireball"],
        collapsed: false,
      },
      // --- Shockwave Ring ---
      {
        id: "ctx_spawn_ring",
        type: "vfx.spawn",
        position: { x: 750, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_ring"],
        collapsed: false,
      },
      {
        id: "ctx_init_ring",
        type: "vfx.initialize",
        position: { x: 750, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_ring_lifetime",
          "init_ring_position",
          "init_ring_velocity",
          "init_ring_size",
          "init_ring_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_ring",
        type: "vfx.update",
        position: { x: 750, y: 750 },
        size: { width: 280, height: 200 },
        nodeIds: ["update_ring_alpha", "update_ring_size"],
        collapsed: false,
      },
      {
        id: "ctx_output_ring",
        type: "vfx.output",
        position: { x: 750, y: 1000 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_ring"],
        collapsed: false,
      },
      // --- Sparks ---
      {
        id: "ctx_spawn_sparks",
        type: "vfx.spawn",
        position: { x: 1100, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_sparks"],
        collapsed: false,
      },
      {
        id: "ctx_init_sparks",
        type: "vfx.initialize",
        position: { x: 1100, y: 200 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "init_sparks_lifetime",
          "init_sparks_velocity",
          "init_sparks_size",
          "init_sparks_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_sparks",
        type: "vfx.update",
        position: { x: 1100, y: 650 },
        size: { width: 280, height: 300 },
        nodeIds: [
          "update_sparks_gravity",
          "update_sparks_drag",
          "update_sparks_alpha",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_sparks",
        type: "vfx.output",
        position: { x: 1100, y: 1000 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_sparks"],
        collapsed: false,
      },
      // --- Smoke ---
      {
        id: "ctx_spawn_smoke",
        type: "vfx.spawn",
        position: { x: 1450, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_smoke"],
        collapsed: false,
      },
      {
        id: "ctx_init_smoke",
        type: "vfx.initialize",
        position: { x: 1450, y: 200 },
        size: { width: 280, height: 600 },
        nodeIds: [
          "init_smoke_lifetime",
          "init_smoke_position",
          "init_smoke_velocity",
          "init_smoke_size",
          "init_smoke_color",
          "init_smoke_angular",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_smoke",
        type: "vfx.update",
        position: { x: 1450, y: 850 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "update_smoke_curl",
          "update_smoke_drag",
          "update_smoke_rotation",
          "update_smoke_size",
          "update_smoke_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_smoke",
        type: "vfx.output",
        position: { x: 1450, y: 1400 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_smoke"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      // Flash flow
      {
        id: "flow_flash_1",
        fromContextId: "ctx_spawn_flash",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_flash",
        toPortId: "spawn_event",
      },
      {
        id: "flow_flash_2",
        fromContextId: "ctx_init_flash",
        fromPortId: "particle",
        toContextId: "ctx_update_flash",
        toPortId: "particle",
      },
      {
        id: "flow_flash_3",
        fromContextId: "ctx_update_flash",
        fromPortId: "particle",
        toContextId: "ctx_output_flash",
        toPortId: "particle",
      },
      // Fireball flow
      {
        id: "flow_fireball_1",
        fromContextId: "ctx_spawn_fireball",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_fireball",
        toPortId: "spawn_event",
      },
      {
        id: "flow_fireball_2",
        fromContextId: "ctx_init_fireball",
        fromPortId: "particle",
        toContextId: "ctx_update_fireball",
        toPortId: "particle",
      },
      {
        id: "flow_fireball_3",
        fromContextId: "ctx_update_fireball",
        fromPortId: "particle",
        toContextId: "ctx_output_fireball",
        toPortId: "particle",
      },
      // Ring flow
      {
        id: "flow_ring_1",
        fromContextId: "ctx_spawn_ring",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_ring",
        toPortId: "spawn_event",
      },
      {
        id: "flow_ring_2",
        fromContextId: "ctx_init_ring",
        fromPortId: "particle",
        toContextId: "ctx_update_ring",
        toPortId: "particle",
      },
      {
        id: "flow_ring_3",
        fromContextId: "ctx_update_ring",
        fromPortId: "particle",
        toContextId: "ctx_output_ring",
        toPortId: "particle",
      },
      // Sparks flow
      {
        id: "flow_sparks_1",
        fromContextId: "ctx_spawn_sparks",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_sparks",
        toPortId: "spawn_event",
      },
      {
        id: "flow_sparks_2",
        fromContextId: "ctx_init_sparks",
        fromPortId: "particle",
        toContextId: "ctx_update_sparks",
        toPortId: "particle",
      },
      {
        id: "flow_sparks_3",
        fromContextId: "ctx_update_sparks",
        fromPortId: "particle",
        toContextId: "ctx_output_sparks",
        toPortId: "particle",
      },
      // Smoke flow
      {
        id: "flow_smoke_1",
        fromContextId: "ctx_spawn_smoke",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_smoke",
        toPortId: "spawn_event",
      },
      {
        id: "flow_smoke_2",
        fromContextId: "ctx_init_smoke",
        fromPortId: "particle",
        toContextId: "ctx_update_smoke",
        toPortId: "particle",
      },
      {
        id: "flow_smoke_3",
        fromContextId: "ctx_update_smoke",
        fromPortId: "particle",
        toContextId: "ctx_output_smoke",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init_flash: { capacity: 10, boundsMode: "auto" },
      ctx_init_fireball: { capacity: 200, boundsMode: "auto" },
      ctx_init_ring: { capacity: 100, boundsMode: "auto" },
      ctx_init_sparks: { capacity: 200, boundsMode: "auto" },
      ctx_init_smoke: { capacity: 100, boundsMode: "auto" },
    },
    capacity: { maxParticles: 600, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "byAge", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}
